import StringResolutionManager from 'models/managers/StringResolutionManager';
import {
  LastSyncedUpstreamValue,
  SchemaFieldColumnUpdate,
  StructuredTable,
  StructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cells} from 'notebook/tables/constants/RowConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {removeQueryResolutionsFromSnapshot} from 'notebook/tables/excess/Resolver';
import * as RefreshTableDataDAO from 'notebook/tables/refresh-table-data/RefreshTableDataDAO';
import {
  EntitiesStructuredTableRowUpdaterRefreshArgs,
  refreshEntitiesInStructuredTableUsingRowUpdater,
  StructuredTableRowUpdaterRefreshArgs,
  updateStructuredTableUsingRowUpdater,
} from 'notebook/tables/refresh-table-data/updateStructuredTableUsingRowUpdater';

export async function fetchColumnUpdatesForTable({
  entryOrTemplateId,
  provenanceUuid,
}: {
  entryOrTemplateId: string;
  provenanceUuid: string;
}): Promise<{[columnId: number]: SchemaFieldColumnUpdate | {}}> {
  const {columnUpdates} = await RefreshTableDataDAO.getColumnUpdates(entryOrTemplateId, provenanceUuid);
  return columnUpdates;
}

type OnUpdateTable = ({
  updatedCells,
  columnUpdates,
  lastSyncedUpstreamValuesByRow,
  numApiRowsToAdd,
}: {
  updatedCells: Cells;
  columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
  lastSyncedUpstreamValuesByRow: Array<Array<LastSyncedUpstreamValue>>;
  numApiRowsToAdd?: number;
}) => void;

interface RefreshParams {
  entryOrTemplateId: string;
  computedTable: Array<Array<EvaluatedCell>>;
  structuredTable: StructuredTable<unknown, unknown>;
  structuredTableAdapter: StructuredTableAdapter<unknown, unknown>;
  onUpdateTable: OnUpdateTable;
}

/**
 * Shared implementation for both structured table refresh variants. Handles all the
 * boilerplate (cache clearing, sample fetching, LSUV extraction, column update fetching)
 * and delegates the core row-update algorithm to `updateFn`.
 */
async function runStructuredTableRefresh(
  {entryOrTemplateId, computedTable, structuredTable, structuredTableAdapter, onUpdateTable}: RefreshParams,
  updateFn: (
    args:
      | StructuredTableRowUpdaterRefreshArgs<unknown, unknown>
      | EntitiesStructuredTableRowUpdaterRefreshArgs<unknown, unknown>
  ) => Promise<StructuredTable<unknown, unknown>>
): Promise<void> {
  StringResolutionManager.clearStringResolutionReduxCache();
  removeQueryResolutionsFromSnapshot(entryOrTemplateId, structuredTable.provenanceUuid, {
    [structuredTable.name]: {type: 'eval-table'},
  });

  const samplesByRowId =
    computedTable.length > 0
      ? structuredTableAdapter.getPrimarySamples(structuredTable, computedTable)
      : structuredTableAdapter.getPrimarySamples(structuredTable);

  const updatedTable = await updateFn({
    rowUpdaters: structuredTableAdapter.getAllRowUpdaters(structuredTable.columns),
    samplesByRowId,
    structuredTable,
    computedTable,
  });

  const lastSyncedUpstreamValuesByRow = updatedTable.rows.reduce(
    (valuesByRow, row) => [...valuesByRow, row.lastSyncedUpstreamValues ?? []],
    [] as Array<Array<LastSyncedUpstreamValue>>
  );

  const table = structuredTableAdapter.toTable(updatedTable);
  const columnUpdates = await fetchColumnUpdatesForTable({
    entryOrTemplateId,
    provenanceUuid: structuredTable.provenanceUuid,
  });

  onUpdateTable({
    updatedCells: table.table,
    columnUpdates,
    lastSyncedUpstreamValuesByRow,
    numApiRowsToAdd: structuredTable.numApiRowsToAdd,
  });
}

export function handleRefreshStructuredTable(params: RefreshParams): Promise<void> {
  return runStructuredTableRefresh(params, updateStructuredTableUsingRowUpdater);
}

/**
 * Partially non-destructive alternative to handleRefreshStructuredTable.
 *
 * Fetches fresh entity data and updates each cell according to its state:
 *   - Unsubmitted rows: all non-formula cells are advanced to the live value; formula cells are
 *     preserved and their LSUV entries are not updated.
 *   - Submitted rows: clean cells (value matches LSUV, no formula) are advanced; formula cells and
 *     user-edited cells (value differs from LSUV) are left unchanged.
 * LSUVs are updated to the live entity value on submitted rows; on unsubmitted rows, only
 * non-formula columns receive updated LSUVs.
 * See refreshEntitiesInStructuredTableUsingRowUpdater for the full per-row semantics.
 */
export function handleRefreshEntitiesInStructuredTable(params: RefreshParams): Promise<void> {
  return runStructuredTableRefresh(params, refreshEntitiesInStructuredTableUsingRowUpdater);
}
